import PropTypes from 'prop-types';

import { useMemo, useState } from 'react';

// @mui
import { alpha, styled } from '@mui/material/styles';
import { Autocomplete, InputAdornment, Chip, TextField, Stack } from '@mui/material';

// components
import Iconify from '../../../components/iconify';


// ----------------------------------------------------------------------

BlogPostsSearch.propTypes = {
  posts: PropTypes.array.isRequired,
  onSearch: PropTypes.func,
  onSort: PropTypes.func,
  postsCount: PropTypes.number
};

const SORT_OPTIONS = ["Latest", "A-Z", "Shuffle"];

const StyledChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  borderRadius: 20,
  fontWeight: 500,
  fontSize: '0.8125rem',
  transition: 'all 0.2s ease',
  ...(selected
    ? {
        background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
        color: '#fff',
        border: 'none',
        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.35)}`,
        '&:hover': {
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.45)}`,
        },
      }
    : {
        borderColor: alpha(theme.palette.grey[500], 0.3),
        '&:hover': {
          borderColor: theme.palette.primary.main,
          backgroundColor: alpha(theme.palette.primary.main, 0.06),
        },
      }),
}));

export default function BlogPostsSearch({ posts, onSearch, onSort, postsCount }) {
  const [sortOption, setSortOption] = useState(SORT_OPTIONS[2]);

  const sortedPosts = useMemo(() => [...posts].sort((a, b) => {
    const nameA = a.folder_name.toUpperCase();
    const nameB = b.folder_name.toUpperCase();
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  }), [posts]);

  const handleSort = (label) => {
    setSortOption(label);
    onSort(label);
  };

  return (
    <Stack spacing={1.5} sx={{ width: '100%' }}>
      <Autocomplete
        freeSolo
        options={sortedPosts}
        getOptionLabel={(post) => (typeof post === 'string' ? post : post.folder_name)}
        isOptionEqualToValue={(option, value) => option.folder_name === value.folder_name}
        onInputChange={(event, newInputValue) => {
          onSearch(newInputValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={`Search ${postsCount} collections…`}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon={'eva:search-fill'} sx={{ ml: 1, width: 20, height: 20, color: 'text.disabled' }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: 3,
                backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.8),
              },
            }}
          />
        )}
      />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {SORT_OPTIONS.map((option) => (
          <StyledChip
            key={option}
            label={option}
            variant={sortOption === option ? 'filled' : 'outlined'}
            selected={sortOption === option}
            onClick={() => handleSort(option)}
            clickable
          />
        ))}
      </Stack>
    </Stack>
  );
}
